import { Grid, Heading, Text } from "@chakra-ui/react"
import { Paper } from "@material-ui/core"
import Container from "@material-ui/core/Container"
import CssBaseline from "@material-ui/core/CssBaseline"
import axios from "axios"
import ChakraInput from "components/Misc/FormComponents/ChakraInput"
import SelectFormField from "components/Misc/FormComponents/SelectFormField"
import SubmitBtn from "components/Misc/FormComponents/SubmitBtn"
import { Field, Form, Formik } from "formik"
import React, { useMemo, useState } from "react"
import { useHistory } from "react-router-dom"
import * as yup from "yup"
import { api, apiLink } from "../../config"
import QuizzesSubForm from "./QuizzesSubForm"
import AsyncSelect from "react-select/async"
import { FormControl, FormLabel, FormErrorMessage } from "@chakra-ui/react"

yup.addMethod(
  yup.array,
  "unique",
  function (message: any, mapper = (a: any) => a) {
    return this.test("unique", message, function (list) {
      return list.length === new Set(list.map(mapper)).size
    })
  },
)

const schema = yup.object({
  code: yup.string().required("Code is required"),
  topic: yup.string().required("Topic is required"),
  subTopic: yup.string().required("Sub Topic is required"),
  category: yup.string().required("Category is required"),
  quizzes: yup
    .array()
    .of(
      yup.object().shape({
        qNo: yup.string().required("Quiz No is required"),
        question: yup.string().required("Question is required"),
        isTrue: yup.boolean(),
      }),
    )
    .required("Quizzes are required")
    .min(1)
    .typeError("At least 1 quiz must be selected"),
})

type Paper = {
  id: number
  topic: string
  subTopic: string
  code: string
  category: string
  paper_quizzes: {
    qNo: string
    question: string
    isTrue: boolean
    image: string
    italianVoiceRecord: string
    sinhalaVoiceRecord: string
    sinhalaText: string
  }[]
}

const QuizzesForm: React.FC<{ paper?: Paper }> = ({ paper }) => {
  const history = useHistory()
  const [loading, setLoading] = useState(false)

  const handleSubmit = async ({
    topic,
    subTopic,
    code,
    category,
    quizzes,
  }: any) => {
    setLoading(true)

    const data = quizzes.map((quiz: any) => ({
      qNo: parseInt(quiz.qNo),
      question: quiz.question,
      isTrue: quiz.isTrue,
      image: quiz.image,
      italianVoiceRecord: quiz.italianVoiceRecord,
      sinhalaVoiceRecord: quiz.sinhalaVoiceRecord,
      sinhalaText: quiz.sinhalaText,
    }))

    try {
      if (paper) {
        await axios.put(
          `${apiLink}/papers/${paper.id}`,
          { topic, subTopic, code, category, paper_quizzes: data },
          { withCredentials: true },
        )
      } else {
        await axios.post(
          `${api}/papers`,
          { topic, subTopic, code, category, paper_quizzes: data },
          { withCredentials: true },
        )
      }
      history.push(`/admin/papers/${category.toLowerCase()}`)
    } catch (err) {
      console.log(err)
    }

    setLoading(false)
  }

  const paperQuizzes = useMemo(() => {
    return paper
      ? paper.paper_quizzes.sort((a: any, b: any) => a.qNo - b.qNo)
      : [
          {
            qNo: "",
            question: "",
            isTrue: false,
            image: "",
            italianVoiceRecord: "",
            sinhalaVoiceRecord: "",
            sinhalaText: "",
          },
        ]
  }, [paper])

  const loadTopics = async (input: string) => {
    if (!input) return []
    const res = await axios.get(`${apiLink}/topics?Name_contains=${input}`)
    return res.data.map((t: any) => ({
      label: t.Name,
      value: t.id,
    }))
  }

  const loadSubTopics = async (input: string, topicId: string) => {
    if (!input || !topicId) return []
    const res = await axios.get(`${apiLink}/sub-topics`, {
      params: {
        Name_contains: input,
        topic: topicId,
      },
    })
    return res.data.map((st: any) => ({
      label: st.Name,
      value: st.id,
    }))
  }

  return (
    <Paper>
      <Container component="main" maxWidth="lg">
        <CssBaseline />

        <Formik
          onSubmit={handleSubmit}
          initialValues={{
            code: paper?.code || "",
            topic: paper?.topic || "",
            subTopic: paper?.subTopic || "",
            category: paper?.category || "",
            quizzes: paperQuizzes,
          }}
          validationSchema={schema}
          validateOnChange={false}
        >
          {({ values, isValid, validateForm, setFieldValue }) => (
            <Form>
              <Heading
                fontSize="xl"
                textAlign="center"
                pt="4"
                fontFamily="Lexend, sans-serif"
              >
                Add Quizzes
              </Heading>

              <Grid
                templateColumns={{ base: "1fr", md: "1fr 2fr" }}
                gap="3"
                alignItems="center"
              >
                <Field
                  label="Code"
                  name="code"
                  component={ChakraInput}
                  fullWidth
                />

                <Field name="topic">
                  {({ form }: any) => (
                    <FormControl>
                      <FormLabel>Topic</FormLabel>

                      <AsyncSelect
                        cacheOptions
                        defaultOptions={false}
                        placeholder="Type Topic"
                        loadOptions={loadTopics}
                        onChange={(opt: any) => {
                          form.setFieldValue("topic", opt?.value || "")
                          form.setFieldValue("subTopic", "")
                        }}
                      />
                    </FormControl>
                  )}
                </Field>

                <Field
                  name="category"
                  component={SelectFormField}
                  label="Category"
                  options={[
                    { label: "Theory Paper", value: "TP" },
                    { label: "Theory Weekend Paper", value: "TWP" },
                    { label: "Revision Paper", value: "RP" },
                    { label: "Revision Weekend Paper", value: "RWP" },
                  ]}
                />

                <Field name="subTopic">
                  {({ form, meta }: any) => (
                    <FormControl
                      isInvalid={meta.touched && Boolean(meta.error)}
                      isDisabled={!form.values.topic}
                    >
                      <FormLabel>Sub Topic</FormLabel>

                      <AsyncSelect
                        cacheOptions
                        defaultOptions={false}
                        isDisabled={!form.values.topic}
                        placeholder={
                          form.values.topic
                            ? "Type Sub Topic"
                            : "Select Topic first"
                        }
                        loadOptions={(input) =>
                          loadSubTopics(input, form.values.topic)
                        }
                        onChange={(opt: any) =>
                          form.setFieldValue("subTopic", opt?.value || "")
                        }
                      />

                      <FormErrorMessage>{meta.error}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              </Grid>

              <br />

              <QuizzesSubForm
                quizzes={values.quizzes}
                setFieldValue={setFieldValue}
                validateForm={validateForm}
              />

              <SubmitBtn
                loading={loading}
                disabled={!isValid || loading}
                w="100%"
                my="3"
              />
            </Form>
          )}
        </Formik>
      </Container>
    </Paper>
  )
}

export default QuizzesForm
